/**
 * EmptyHive — the ONE empty-state component for the app.
 *
 * Replaces every "No results found" / "Nothing here yet" surface in Bearri
 * with a bear mascot + voicey copy + optional CTA. Part of the Hive Design
 * System v2.
 *
 * Uses <picture> with WebP sources + PNG fallback for ~85% byte savings on
 * supporting browsers (Safari 14+, Chrome, Firefox, Edge).
 */
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import bearriReading from '../../assets/images/bearri_art/bearri_reading.png';
import bearriReadingWebp from '../../assets/images/bearri_art/bearri_reading.webp';
import bearriThinking from '../../assets/images/bearri_art/bearri_thinking.png';
import bearriThinkingWebp from '../../assets/images/bearri_art/bearri_thinking.webp';
import bearriError from '../../assets/images/bearri_art/bearri_error.png';
import bearriErrorWebp from '../../assets/images/bearri_art/bearri_error.webp';
import bearriSleeping from '../../assets/images/bearri_art/bearri_sleeping.png';
import bearriSleepingWebp from '../../assets/images/bearri_art/bearri_sleeping.webp';
import bearriGift from '../../assets/images/bearri_art/bearri_gift.png';
import bearriGiftWebp from '../../assets/images/bearri_art/bearri_gift.webp';
import bearriBerry from '../../assets/images/bearri_art/Bearri_Berry_Picking.png';
import bearriBerryWebp from '../../assets/images/bearri_art/Bearri_Berry_Picking.webp';

type Illustration = 'reading' | 'thinking' | 'error' | 'sleeping' | 'gift' | 'berry' | 'jar';

type MascotSources = { png: string; webp: string };

const MASCOTS: Record<Exclude<Illustration, 'jar'>, MascotSources> = {
  reading:  { png: bearriReading,  webp: bearriReadingWebp  },
  thinking: { png: bearriThinking, webp: bearriThinkingWebp },
  error:    { png: bearriError,    webp: bearriErrorWebp    },
  sleeping: { png: bearriSleeping, webp: bearriSleepingWebp },
  gift:     { png: bearriGift,     webp: bearriGiftWebp     },
  berry:    { png: bearriBerry,    webp: bearriBerryWebp    },
};

const HONEY_JAR: MascotSources = {
  png: '/bearri_art/honey-jar.png',
  webp: '/bearri_art/honey-jar.webp',
};

interface Props {
  illustration?: Illustration;
  headline: string;
  body?: string;
  action?: { label: string; onClick?: () => void; href?: string };
  className?: string;
}

// framer-motion's <motion.img> doesn't wrap <picture>, so we build a
// motion.div container and put the <picture> inside it. This still preserves
// the entrance animation while letting the browser pick webp when supported.
export const EmptyHive: React.FC<Props> = ({
  illustration = 'reading',
  headline,
  body,
  action,
  className = '',
}) => {
  const reduce = useReducedMotion();
  const sources: MascotSources = illustration === 'jar' ? HONEY_JAR : MASCOTS[illustration];

  return (
    <div
      className={`flex flex-col items-center justify-center text-center py-12 px-6 ${className}`}
      role="status"
    >
      <motion.div
        className="w-36 h-36 md:w-44 md:h-44 mb-6 select-none pointer-events-none"
        initial={reduce ? {} : { opacity: 0, y: 12, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <picture>
          <source srcSet={sources.webp} type="image/webp" />
          <img
            src={sources.png}
            alt=""
            aria-hidden
            className="w-full h-full object-contain"
            draggable={false}
          />
        </picture>
      </motion.div>
      <h3 className="text-h3 font-display font-semibold text-text-primary mb-2">
        {headline}
      </h3>
      {body && (
        <p className="text-body max-w-md text-text-secondary mb-5">
          {body}
        </p>
      )}
      {action && (
        action.href ? (
          <a
            href={action.href}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-bearriBlue text-white font-medium hover:bg-bearriBlue-dark transition-colors"
          >
            {action.label}
          </a>
        ) : (
          <button
            type="button"
            onClick={action.onClick}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-bearriBlue text-white font-medium hover:bg-bearriBlue-dark transition-colors"
          >
            {action.label}
          </button>
        )
      )}
    </div>
  );
};

export default EmptyHive;
